class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = Object.create(null);
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.requestVersion = 0;

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.addEventListener('input', () => {
      this.requestVersion++;
      this.abortController.abort();
      this.close();
      this.removeAttribute('results');
    });
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('[id$="predictive-search-results-groups-wrapper"]')?.remove();
    }
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'Escape':
        this.requestVersion++;
        this.abortController.abort();
        this.close();
        break;
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const label = this.querySelector('[data-predictive-search-search-for-text]');
    if (!label || !previousTerm) return;
    const text = label.textContent;
    const first = text.indexOf(previousTerm);
    if (first < 0 || text.indexOf(previousTerm, first + previousTerm.length) >= 0) return;
    label.textContent = text.slice(0, first) + newTerm + text.slice(first + previousTerm.length);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    if (this.statusElement) this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];
    if (!activeElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  async getSearchResults(searchTerm) {
    const version = ++this.requestVersion;
    this.abortController.abort();
    this.abortController = new AbortController();
    const queryKey = searchTerm.toLocaleLowerCase();
    this.setLiveRegionLoadingState();
    const current = () => version === this.requestVersion && this.getQuery() === searchTerm && this.input === document.activeElement;
    try {
      if (this.cachedResults[queryKey]) {
        if (current()) this.renderSearchResults(this.cachedResults[queryKey]);
        return;
      }
      const url = new URL(routes.predictive_search_url, location.origin);
      url.searchParams.set('q', searchTerm);
      url.searchParams.set('section_id', 'predictive-search');
      const response = await fetch(url, { signal: this.abortController.signal });
      if (!response.ok) throw new Error('search_unavailable');
      const text = await response.text();
      const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
      if (!section) throw new Error('search_unavailable');
      this.allPredictiveSearchInstances.forEach(instance => { instance.cachedResults[queryKey] = section.innerHTML; });
      if (current()) this.renderSearchResults(section.innerHTML);
    } catch (error) {
      if (error.name === 'AbortError' || !current()) return;
      this.predictiveSearchResults.replaceChildren();
      const message = document.createElement('p');
      message.textContent = this.dataset.errorText;
      const link = document.createElement('a');
      const url = new URL(this.input.form.action, location.origin);
      url.searchParams.set('q', searchTerm);
      link.href = url.href;
      link.textContent = this.dataset.retryText;
      link.className = 'predictive-search__item link';
      link.tabIndex = 0;
      this.predictiveSearchResults.append(message, link);
      this.removeAttribute('loading');
      this.setLiveRegionText(this.dataset.errorText);
      this.open();
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    const prefix = this.input.id + '-';
    this.predictiveSearchResults.querySelectorAll('[id]').forEach(element => { element.id = prefix + element.id; });
    this.predictiveSearchResults.querySelectorAll('[aria-labelledby]').forEach(element => {
      element.setAttribute('aria-labelledby', element.getAttribute('aria-labelledby').split(' ').map(id => prefix + id).join(' '));
    });
    this.input.setAttribute('aria-controls', prefix + 'predictive-search-results');
    this.input.setAttribute('aria-owns', prefix + 'predictive-search-results');
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || '');
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header .header-wrapper').getBoundingClientRect().bottom;
      const getHeader = document.querySelector('.section-header');
      const getHeaderType = getHeader.classList.contains('shopify-section-header-cover');
      if (getHeaderType === true) this.resultsMaxHeight = this.resultsMaxHeight - 120;
      if (this.resultsMaxHeight > 600) this.resultsMaxHeight = 600;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
